/**
 * usePrintCollection hook — manage a collection of prints for batch operations.
 * Replaces window.usePrintCollection.
 */
import { useState, useCallback, useRef } from 'react';
import * as dataService from '../services/dataService';
import * as renderService from '../services/renderService';
import type { Product, FileItem, PrintItem } from '../stores/appStore';
import type { Transform } from '../stores/types';

export function usePrintCollection() {
  const [printCollection, setPrintCollection] = useState<PrintItem[]>([]);
  const [selectedPrintIds, setSelectedPrintIds] = useState<string[]>([]);
  const pendingSkusRef = useRef(new Set<string>());

  const addPrintToCollection = useCallback(
    async (
      file: FileItem,
      products: Product[],
      transforms: Record<string, Transform>,
      productTransforms: Record<string, Transform>,
      _activeTab: string,
    ): Promise<PrintItem | null> => {
      if (!file) return null;
      try {
        let article = file.article || '';
        let resolvedPrintName = file.print_name || '';

        // Server lookup
        if (!article) {
          const found = await dataService.findArticleBySource(file.name);
          if (found?.article) {
            article = found.article;
            if (!resolvedPrintName && found.print_name) resolvedPrintName = found.print_name;
          }
        }

        // Generate SKU
        if (!article) {
          const serverSku = await dataService.generateSku();
          if (serverSku) {
            article = serverSku;
            // Local collision check
            const isBusy = (art: string) =>
              printCollection.some((p) => p.article === art) || pendingSkusRef.current.has(art);
            while (isBusy(article)) {
              const m = article.match(/(\d+)$/);
              if (m) {
                const prefix =
                  article.substring(0, article.length - m[1].length) || article.slice(0, 6);
                const suffix = m[1];
                article =
                  prefix + String(parseInt(suffix || '0', 10) + 1).padStart(suffix.length, '0');
              } else {
                article = article + '1';
              }
            }
            pendingSkusRef.current.add(article);
          } else {
            article = file.name.split('.')[0];
          }
        }

        const printId = 'print_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
        const enabledProducts = products.filter((p) => p.enabled);
        const positions: Record<string, { mockups: Transform; products: Transform }> = {};

        enabledProducts.forEach((prod) => {
          positions[prod.id] = {
            mockups:
              transforms[prod.id] ||
              renderService.getTransformByMode(
                transforms,
                productTransforms,
                'mockups',
                prod.id,
                0.5,
              ),
            products:
              productTransforms[prod.id] ||
              renderService.getTransformByMode(
                transforms,
                productTransforms,
                'products',
                prod.id,
                0.6,
              ),
          };
        });

        const newPrint: PrintItem = {
          id: printId,
          name: file.name,
          url: file.url,
          thumb: file.thumb || file.url,
          article,
          printName: resolvedPrintName,
          positions,
        };

        setPrintCollection((prev) => [...prev, newPrint]);
        setSelectedPrintIds((prev) => [...prev, printId]);
        return newPrint;
      } catch (e) {
        console.error('Ошибка при добавлении принта в коллекцию:', e);
        throw e;
      }
    },
    [printCollection],
  );

  const selectPrintInCollection = useCallback(
    (printId: string) => {
      setSelectedPrintIds((prev) => (prev.includes(printId) ? prev : [...prev, printId]));
      return printCollection.find((p) => p.id === printId) || null;
    },
    [printCollection],
  );

  const removePrintFromCollection = useCallback((printId: string) => {
    setPrintCollection((prev) => {
      const printToRemove = prev.find((p) => p.id === printId);
      const next = prev.filter((p) => p.id !== printId);
      if (printToRemove?.article) {
        const isUsed = next.some((p) => p.article === printToRemove.article);
        if (!isUsed) pendingSkusRef.current.delete(printToRemove.article);
      }
      return next;
    });
    setSelectedPrintIds((prev) => prev.filter((id) => id !== printId));
  }, []);

  const updateArticle = useCallback((printId: string, newArticle: string) => {
    setPrintCollection((prev) => {
      const p = prev.find((p) => p.id === printId);
      if (p) {
        const others = prev.some((item) => item.id !== printId && item.article === p.article);
        if (!others) pendingSkusRef.current.delete(p.article);
      }
      return prev.map((p) => (p.id === printId ? { ...p, article: newArticle } : p));
    });
  }, []);

  const updatePrintName = useCallback((printId: string, newPrintName: string) => {
    setPrintCollection((prev) =>
      prev.map((p) => (p.id === printId ? { ...p, printName: newPrintName } : p)),
    );
  }, []);

  const removeByFileName = useCallback((fileName: string) => {
    setPrintCollection((prev) => {
      const next = prev.filter((p) => p.name !== fileName);
      const removed = prev.filter((p) => p.name === fileName);
      removed.forEach((r) => {
        if (r.article && !next.some((p) => p.article === r.article)) {
          pendingSkusRef.current.delete(r.article);
        }
      });
      setSelectedPrintIds((ids) => ids.filter((id) => next.some((p) => p.id === id)));
      return next;
    });
  }, []);

  const getPrintsByIds = useCallback(
    (printIds: string[]) => {
      return printCollection.filter((p) => printIds.includes(p.id));
    },
    [printCollection],
  );

  const removePrintsByIds = useCallback((printIds: string[]) => {
    setPrintCollection((prev) => {
      const next = prev.filter((p) => !printIds.includes(p.id));
      const removed = prev.filter((p) => printIds.includes(p.id));
      removed.forEach((r) => {
        if (r.article && !next.some((p) => p.article === r.article)) {
          pendingSkusRef.current.delete(r.article);
        }
      });
      return next;
    });
    setSelectedPrintIds([]);
  }, []);

  const updatePositions = useCallback((printId: string, newPositions: PrintItem['positions']) => {
    setPrintCollection((prev) =>
      prev.map((p) => (p.id === printId ? { ...p, positions: newPositions } : p)),
    );
  }, []);

  return {
    printCollection,
    selectedPrintIds,
    addPrintToCollection,
    selectPrintInCollection,
    removePrintFromCollection,
    updateArticle,
    updatePrintName,
    removeByFileName,
    getPrintsByIds,
    removePrintsByIds,
    updatePositions,
  };
}
